import { jwtDecode } from 'jwt-decode'
import { Token } from 'types/api'
import { StoredUser, TokenRefresh } from '../../features/auth/types'

export const getTimestampInSeconds = (): number => {
  return Math.floor(Date.now() / 1000)
}

export const USER_STORAGE_KEY = 'user'

export interface IUserStorage {
  /**
   * Saves the provided user to the local storage by replacing the existing one.
   *
   * @param email User email.
   * @param tokens Tokens object.
   */
  save(tokens: Token): void
  /**
   * Retrieves the user from the local storage if found.
   */
  get(): StoredUser | undefined
  /**
   * Removes all the user-associated data from local storage.
   */
  clear(): void
  /**
   * Replaces tokens for the stored user.
   *
   * @param tokens Tokens object.
   */
  setTokens(tokens: TokenRefresh): void
  /**
   * Return Authoirzation header payload with a token from user found in localStorage.
   *
   * @returns Authorization header payload or `undefined`.
   */
  getBearer(): string | undefined
  /**
   * Returns refresh token from the user object found in localStorage.
   *
   * @returns Refresh token or `undefined`.
   */
  getRefresh(): string | undefined
  /**
   * Checks if JWT access token has expired by retrieving the stored user from localStorage.
   * If no user stored – returns `true`.
   *
   * **Important:**
   * To avoid token expiration while requests are in-flight the token will be
   * considered expired shortly before the actual expiration time (see buffer constant).
   *
   * @returns Boolean specifying if the user has expired.
   */
  isExpired(): boolean
}

export const userStorage: IUserStorage = {
  save(tokens) {
    if (!tokens.access) {
      throw new Error('Access token is missing')
    }
    if (!tokens.refresh) {
      throw new Error('Refresh token is missing')
    }
    const decodedToken = jwtDecode<
      TokenRefresh & {
        exp: number
      }
    >(tokens.access)
    const user: StoredUser = {
      accessToken: tokens.access,
      refreshToken: tokens.refresh,
      lastRefresh: getTimestampInSeconds(),
      expiresAt: decodedToken.exp
    }

    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
  },

  get() {
    try {
      const storedUser = localStorage.getItem(USER_STORAGE_KEY)

      if (!storedUser) {
        return
      }

      return JSON.parse(storedUser)
    } catch (error: unknown) {
      console.error('Failed to parse stored user', error)
    }
  },

  clear() {
    localStorage.removeItem(USER_STORAGE_KEY)
  },

  setTokens(tokens) {
    const user = userStorage.get()
    if (user) userStorage.save(tokens)
  },

  getBearer() {
    const user = userStorage.get()
    return user ? `Bearer ${user.accessToken}` : undefined
  },

  getRefresh() {
    const user = userStorage.get()
    return user?.refreshToken
  },

  isExpired() {
    const user = userStorage.get()

    if (!user) {
      return true
    }

    // Refresh this many seconds before JWT `exp`. Derived from the token's
    // actual lifetime (backend SIMPLE_JWT.ACCESS_TOKEN_LIFETIME, currently
    // 15 min) so it works against any backend and can never exceed the
    // lifetime itself, which would cause a refresh per request.
    const lifetime = user.expiresAt - user.lastRefresh
    const buffer = Math.min(60, Math.max(5, Math.floor(lifetime / 3)))

    return user.expiresAt - getTimestampInSeconds() <= buffer
  }
}
